/**
 * Collection grid last-row balancing.
 *
 * Moved verbatim out of an inline <script> in
 * sections/main-collection-product-grid.liquid so the payload is cached and
 * deferred instead of being re-sent with every collection HTML response.
 *
 * Why it exists: some products render as more than one card (colour variants)
 * while others render none, so a page of 15 products can emit 19 cards. In a
 * 3-column grid that strands a single card on the last row, which reads as the
 * end of the collection even though another page is waiting. While the Show
 * more button is present the trailing leftover cards are hidden so the last
 * visible row is always full; they are revealed as soon as the next batch
 * loads, because Load more APPENDS into the same grid.
 *
 * Behaviour is unchanged. The MutationObserver already handled AJAX filtering
 * and Load more; the shopify:section:load hook below restores the one thing an
 * external script would otherwise lose relative to inline markup, namely
 * re-running after the theme editor re-renders the section.
 */
(function () {
  function init() {
    var wrapper = document.getElementById('ProductGridWrapper');
    if (!wrapper) return;

    var classObserver = null;

    function currentGrid() {
      return wrapper.querySelector('#ProductsList');
    }

    // The theme already publishes the active column count per breakpoint as
    // --col-number on the f-grid-*-cols classes, so this follows resizes and the
    // mobile layout switcher without hardcoding any breakpoints.
    function columnCount(grid) {
      var n = parseInt(getComputedStyle(grid).getPropertyValue('--col-number'), 10);
      return n > 0 ? n : 1;
    }

    // Present only while another page is still waiting to be loaded.
    function moreToLoad() {
      return !!wrapper.querySelector('[is="load-more-button"]');
    }

    function balance() {
      var grid = currentGrid();
      if (!grid) return;

      var cards = Array.prototype.slice.call(grid.children);
      cards.forEach(function (card) {
        card.style.removeProperty('display');
      });
      if (!moreToLoad()) return;

      var cols = columnCount(grid);
      var leftover = cards.length % cols;
      // Never hide so much that a full row isn't left standing.
      if (leftover === 0 || cards.length - leftover < cols) return;

      cards.slice(cards.length - leftover).forEach(function (card) {
        card.style.setProperty('display', 'none', 'important');
      });
    }

    function watchLayoutClasses() {
      var grid = currentGrid();
      if (!grid) return;
      if (classObserver) classObserver.disconnect();
      // The mobile layout switcher swaps f-grid-*-cols on the grid itself.
      classObserver = new MutationObserver(balance);
      classObserver.observe(grid, { attributes: true, attributeFilter: ['class'] });
    }

    function refresh() {
      watchLayoutClasses();
      balance();
    }

    // childList only — appended cards (Load more) and a replaced grid (filtering
    // rewrites #ProductGridContainer wholesale). Our own inline display writes are
    // attribute changes, so they cannot retrigger this.
    new MutationObserver(refresh).observe(wrapper, { childList: true, subtree: true });

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(balance, 150);
    });

    refresh();
  }

  init();

  // The theme editor replaces section markup without re-running external
  // scripts, so re-run the initialiser after every section render. init() is
  // self-guarding: it returns immediately when the wrapper is absent, and each
  // run observes only the grid that is currently in the document.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector('#ProductGridWrapper')) {
      init();
    }
  });
})();
